import { useActionState, useState } from "react";
import { Button } from "@/components/Button";
import {
  CheckboxField,
  CheckboxGroup,
  SelectField,
  type SelectOption,
  TextField,
} from "@/components/Field";
import { LinkButton } from "@/components/LinkButton";
import { useCodeFromLabel } from "@/components/useCodeFromLabel";
import { type FormState, initialFormState } from "@/lib/formState";
import {
  CONTENT_GRADES,
  GOAL_TYPES,
  PARTICIPATIONS,
  SETTINGS,
  VERIFICATIONS,
} from "@shared/validation";
import { IcuAssignmentField, type PreviewBinding } from "./IcuAssignmentField";
import { type SkillChoice, SkillFields } from "./SkillFields";
import styles from "./TemplateForm.module.css";
import { type TopicChoice, TopicField } from "./TopicField";
import type { TraitChoice } from "./TraitCombobox";
import { TraitFields, type TraitSelection } from "./TraitFields";
import { TITLE_HINT } from "./wordingCopy";

const toOptions = (values: readonly string[]): SelectOption[] =>
  values.map((value) => ({ value, label: value }));

const NONE_OPTION: SelectOption = { value: "", label: "— none —" };

export type TemplateFormValues = {
  code: string;
  goalType: string;
  metricId: string;
  verification: string;
  topicId: string;
  participation: string[];
  setting: string;
  tags: string;
  cooldownDays: string;
  contentGrade: string;
  hasOpenSubject: boolean;
  title: string;
  assignment: string;
  skillIds: number[];
  requiresTraitIds: number[];
  grantsTraitIds: number[];
};

type TemplateFormProps = {
  action: (state: FormState, formData: FormData) => Promise<FormState>;
  values: TemplateFormValues;
  metricOptions: SelectOption[];
  topics: TopicChoice[];
  localeCode: string;
  bindings: readonly PreviewBinding[];
  // Both are locked by the database once a rung exists — the composite foreign
  // key on goal type, a trigger on metric — so the form says so rather than
  // letting someone try and be rejected.
  hasRungs: boolean;
  // Wording moves to its own form once the template exists, because it is
  // versioned per locale and writing it stales every translation on an edit
  // that never touched it. The bindings have no such problem, so both sets
  // save with the row on either page.
  isWordingIncluded: boolean;
  skills: SkillChoice[];
  traits: TraitChoice[];
  submitLabel: string;
  pendingLabel: string;
};

export const TemplateForm = ({
  action,
  values,
  metricOptions,
  topics,
  localeCode,
  bindings,
  hasRungs,
  isWordingIncluded,
  skills,
  traits,
  submitLabel,
  pendingLabel,
}: TemplateFormProps) => {
  const [state, formAction, isPending] = useActionState(
    action,
    initialFormState,
  );
  const [hasArchivedTrait, setHasArchivedTrait] = useState(false);
  // The code is UPPER_SNAKE of the title until someone edits it themselves, so
  // one owner holds both. On the detail page the title lives in its own form
  // and only the code half of this is in use.
  const {
    label: title,
    code,
    setLabel: setTitle,
    setCode,
  } = useCodeFromLabel(values.title, values.code);

  const trackTraits = (selection: TraitSelection) => {
    setHasArchivedTrait(selection.archivedIds.length > 0);
  };

  const lockNote = hasRungs
    ? "Locked: this template has rungs, and changing it would alter what in-flight attempts measure."
    : undefined;

  return (
    <form action={formAction} className={styles.form}>
      {/* Both or neither, and adjacent: the two are written in one pass, and
          the rule that they must never restate each other is unreadable with
          the classification grid sitting between them. */}
      {isWordingIncluded ? (
        <>
          <TextField
            name="title"
            label="Title"
            hint={TITLE_HINT}
            placeholder="Emoji presentation"
            value={title}
            onValueChange={setTitle}
            errors={state.fieldErrors.title}
          />
          <IcuAssignmentField
            name="assignment"
            label="Assignment"
            localeCode={localeCode}
            defaultValue={state.values.assignment ?? values.assignment}
            errors={state.fieldErrors.assignment}
            bindings={bindings}
          />
        </>
      ) : null}

      <div className={styles.grid}>
        <SelectField
          name="goalType"
          label="Goal type"
          hint={
            lockNote ??
            "THRESHOLD counts up to a target, MANUAL is self-reported with no number. How often it must be done is a rung's business, not this."
          }
          options={toOptions(GOAL_TYPES)}
          defaultValue={state.values.goalType ?? values.goalType}
          errors={state.fieldErrors.goalType}
          isDisabled={hasRungs}
        />
        <SelectField
          name="metricId"
          label="Metric"
          hint={
            lockNote ?? "Required before publishing unless the goal is MANUAL."
          }
          options={[NONE_OPTION, ...metricOptions]}
          defaultValue={state.values.metricId ?? values.metricId}
          errors={state.fieldErrors.metricId}
          isDisabled={hasRungs}
        />
        <SelectField
          name="verification"
          label="Verification"
          hint="AUTO needs a metric fed by an integration; anything else is self-reported."
          options={toOptions(VERIFICATIONS)}
          defaultValue={state.values.verification ?? values.verification}
          errors={state.fieldErrors.verification}
        />
        <TopicField
          topics={topics}
          selectedId={state.values.topicId ?? values.topicId}
          errors={state.fieldErrors.topicId}
        />
        <SelectField
          name="setting"
          label="Setting"
          hint="Kept out of tags because the daily offer branches on it at 7am when it is raining."
          options={toOptions(SETTINGS)}
          defaultValue={state.values.setting ?? values.setting}
          errors={state.fieldErrors.setting}
        />
        <SelectField
          name="contentGrade"
          label="Content grade"
          hint="Authoring originality, not difficulty. STOCK fills metric and topic coverage; CRAFTED carries an idea. Label and filter only — it never feeds progression."
          options={toOptions(CONTENT_GRADES)}
          defaultValue={state.values.contentGrade ?? values.contentGrade}
          errors={state.fieldErrors.contentGrade}
        />
        <TextField
          name="cooldownDays"
          label="Cooldown days"
          hint="Blank means once, ever. Sits on the template, so completing one rung parks the whole idea."
          type="number"
          defaultValue={state.values.cooldownDays ?? values.cooldownDays}
          errors={state.fieldErrors.cooldownDays}
        />
        <TextField
          name="code"
          label="Code"
          hint="Follows the title until you edit it. Only a handful of templates are named by app code, so clearing it is a normal answer."
          placeholder="ONBOARDING_FIRST_WALK"
          value={code}
          onValueChange={setCode}
          errors={state.fieldErrors.code}
        />
        <TextField
          name="tags"
          label="Tags"
          hint="Comma separated, lower-case-hyphenated. A suggestion vocabulary, not a constraint."
          placeholder="no-equipment, quiet"
          defaultValue={state.values.tags ?? values.tags}
          errors={state.fieldErrors.tags}
        />
      </div>

      <CheckboxField
        name="hasOpenSubject"
        label="The user picks the subject"
        hint="Tick only when the wording hands the choice over — “a topic that interests you”. A fixed subject is not open, and neither is implied freedom like any book or any dish: tick those and every quest is open, which tells us nothing. It records which quests could be specialised into narrower ones; it is never shown to a user and never feeds progression."
        // An unchecked box posts nothing, so a submitted form reports "" for
        // this field — absent means the form has not been submitted yet, not
        // that the box was cleared.
        isChecked={
          state.values.hasOpenSubject === undefined
            ? values.hasOpenSubject
            : state.values.hasOpenSubject !== ""
        }
        errors={state.fieldErrors.hasOpenSubject}
      />

      <CheckboxGroup
        name="participation"
        label="Who it is for"
        hint="PAIR is any two people; COUPLE is romantic. A surprise-date quest must not reach someone doing it with their brother."
        options={toOptions(PARTICIPATIONS)}
        defaultValues={state.listValues?.participation ?? values.participation}
        errors={state.fieldErrors.participation}
      />

      <fieldset className={styles.bindings}>
        <legend className={styles.legend}>Skills</legend>
        <p className={styles.note}>
          Progression counts distinct completed templates per skill, and the
          publish gate demands at least one — so picking none here only defers
          the same decision.
        </p>
        <SkillFields
          skills={skills}
          selectedIds={
            state.listValues?.skillIds?.map(Number) ?? values.skillIds
          }
          errors={state.fieldErrors.skillIds}
        />
      </fieldset>

      <fieldset className={styles.bindings}>
        <legend className={styles.legend}>Traits</legend>
        <p className={styles.note}>
          Traits sit on the template, not the rung — the whole ladder is gated,
          not step three. A draft may leave these empty.
        </p>
        <TraitFields
          traits={traits}
          requiresIds={
            state.listValues?.requiresTraitIds?.map(Number) ??
            values.requiresTraitIds
          }
          grantsIds={
            state.listValues?.grantsTraitIds?.map(Number) ??
            values.grantsTraitIds
          }
          errors={state.fieldErrors.requiresTraitIds}
          onSelectionChange={trackTraits}
        />
      </fieldset>

      {(state.listValues?.createdTags ?? []).length > 0 ? (
        <output className={styles.created}>
          Added to the tag vocabulary:{" "}
          {(state.listValues?.createdTags ?? []).join(", ")}. Tidy them up on
          the Tags page.
        </output>
      ) : null}

      <div className={styles.actions}>
        <Button
          type="submit"
          variant="primary"
          disabled={isPending || hasArchivedTrait}
        >
          {isPending ? pendingLabel : submitLabel}
        </Button>
        <LinkButton href="/quests" variant="secondary">
          Cancel
        </LinkButton>
      </div>
    </form>
  );
};
